import {
  CheckCircleIcon,
  ExclamationTriangleIcon,
  InformationCircleIcon,
  XCircleIcon,
  XMarkIcon
} from "@heroicons/react/20/solid";
import {PropsWithChildren, ReactNode} from "react";

export type OtsInlineAlertTone = "info" | "success" | "warning" | "danger";

export type OtsInlineAlertProps = PropsWithChildren<{
  tone?: OtsInlineAlertTone;
  title?: ReactNode;
  /** A single follow-up, e.g. a Retry button. */
  action?: ReactNode;
  onDismiss?: () => void;
  size?: "compact" | "default";
  className?: string;
}>;

const toneStyles: Record<OtsInlineAlertTone, {container: string; icon: string; Icon: typeof InformationCircleIcon}> = {
  info: {
    container: "border-[var(--ots-outline-variant)] bg-[var(--ots-primary-container)] text-[var(--ots-secondary)]",
    icon: "text-[var(--ots-primary)]",
    Icon: InformationCircleIcon
  },
  success: {
    container: "border-[var(--ots-success-outline)] bg-[var(--ots-success-container)] text-[var(--ots-on-success-container)]",
    icon: "text-[var(--ots-success)]",
    Icon: CheckCircleIcon
  },
  warning: {
    container: "border-[var(--ots-warning-outline)] bg-[var(--ots-warning-container)] text-[var(--ots-on-warning-container)]",
    icon: "text-[var(--ots-warning)]",
    Icon: ExclamationTriangleIcon
  },
  danger: {
    container: "border-[var(--ots-danger-outline)] bg-[var(--ots-danger-container)] text-[var(--ots-on-danger-container)]",
    icon: "text-[var(--ots-error)]",
    Icon: XCircleIcon
  }
};

/** A message in the flow of the page, for outcomes the reader should see but need not stop for. */
export const OtsInlineAlert = ({
  tone = "info",
  title,
  action,
  onDismiss,
  size = "default",
  className,
  children
}: OtsInlineAlertProps) => {
  const {container, icon, Icon} = toneStyles[tone];
  // Problems interrupt a screen reader; confirmations wait their turn.
  const urgent = tone === "danger" || tone === "warning";

  return (
    <div
      role={urgent ? "alert" : "status"}
      className={[
        "flex rounded-lg border",
        size === "compact" ? "gap-2 px-3 py-2 text-xs" : "gap-3 px-4 py-3 text-sm",
        container,
        className
      ].filter(Boolean).join(" ")}>
      <Icon aria-hidden="true" className={["mt-0.5 shrink-0", size === "compact" ? "size-4" : "size-5", icon].join(" ")}/>
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={title ? "mt-1 leading-5" : "leading-5"}>{children}</div>}
        {action && <div className={size === "compact" ? "mt-2" : "mt-3"}>{action}</div>}
      </div>
      {onDismiss && (
        <button
          type="button"
          onClick={onDismiss}
          aria-label="Dismiss"
          className="-m-1 flex size-7 shrink-0 items-center justify-center rounded-md opacity-70 transition-opacity hover:opacity-100 focus-visible:outline-2 focus-visible:outline-[var(--ots-primary)]">
          <XMarkIcon aria-hidden="true" className="size-4"/>
        </button>
      )}
    </div>
  );
};
